import { useState, useMemo } from 'react'
import { useQuery } from 'react-query'
import { intelligenceLabService, type LabRoster, type LabRosterAgent } from '../services/api'
import { AlertCircle, RefreshCw, Search, Wifi, WifiOff, Key, FileText } from 'lucide-react'

function RuntimeBadge({ status }: { status: LabRosterAgent['runtime']['status'] }) {
  if (status === 'healthy') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium badge-success">
        <Wifi className="w-3 h-3" /> healthy
      </span>
    )
  }
  if (status === 'unhealthy') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-error-light text-error border">
        <WifiOff className="w-3 h-3" /> unhealthy
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium badge-inactive">
      <WifiOff className="w-3 h-3" /> unknown
    </span>
  )
}

function ApiKeyBadge({ apiKey }: { apiKey: LabRosterAgent['apiKey'] }) {
  if (apiKey.source === 'env') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-primary border" title={apiKey.envName}>
        <Key className="w-3 h-3" /> env
      </span>
    )
  }
  if (apiKey.source === 'console') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-primary border" title={apiKey.masked}>
        <Key className="w-3 h-3" /> console
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-error-light text-error border">
      <Key className="w-3 h-3" /> absente
    </span>
  )
}

function PromptBadge({ prompt }: { prompt: LabRosterAgent['prompt'] }) {
  if (!prompt.exists) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium badge-inactive">
        <FileText className="w-3 h-3" /> aucun
      </span>
    )
  }
  const color =
    prompt.source === 'runtime'
      ? 'badge-success'
      : prompt.source === 'library'
      ? 'badge-warning'
      : 'bg-blue-50 text-primary border'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`} title={prompt.path ?? undefined}>
      <FileText className="w-3 h-3" /> {prompt.source ?? 'console'}
    </span>
  )
}

// * `roster` vaut 'integria' (collectif réparti en pools) ou 'service' (agent
// * logique autonome). Le badge le lisait comme un tableau d'identifiants : sa
// * longueur étant toujours > 0 pour une chaîne non vide, tous les agents
// * étaient étiquetés « pool », y compris les services.
function TypeBadge({ kind, roster, poolId }: { kind?: string; roster?: string | null; poolId?: string | null }) {
  const label = poolId ? 'pool' : (roster ?? kind ?? '—')
  const color =
    label === 'pool'
      ? 'badge-warning'
      : label === 'service' || label === 'logical_service'
      ? 'bg-blue-50 text-primary border'
      : 'badge-inactive'
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}>
      {label}
    </span>
  )
}

function groupByPool(agents: LabRosterAgent[]): Record<string, LabRosterAgent[]> {
  const groups: Record<string, LabRosterAgent[]> = { '': [] }
  for (const agent of agents) {
    const key = agent.poolId ?? ''
    if (!groups[key]) groups[key] = []
    groups[key].push(agent)
  }
  return groups
}

export function TeamInspector() {
  const [search, setSearch] = useState('')
  const [selectedPool, setSelectedPool] = useState<string>('__all__')

  const {
    data: roster,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<LabRoster, Error>(
    ['intelligence-lab-roster'],
    () => intelligenceLabService.getRoster(),
    { retry: 1, staleTime: 30_000 },
  )

  const agents = roster?.agents
  const groups = useMemo(() => (agents ? groupByPool(agents) : {}), [agents])
  const poolIds = useMemo(() => Object.keys(groups).filter(k => k !== ''), [groups])

  const filtered = useMemo(() => {
    if (!agents) return []
    return agents.filter(agent => {
      const matchSearch = !search || agent.name.toLowerCase().includes(search.toLowerCase()) || agent.id.toLowerCase().includes(search.toLowerCase())
      const matchPool =
        selectedPool === '__all__' ||
        (selectedPool === '__none__' && !agent.poolId) ||
        agent.poolId === selectedPool
      return matchSearch && matchPool
    })
  }, [agents, search, selectedPool])

  return (
    <div className="page-inset p-6 space-y-6">
      <div className="page-toolbar flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary">Inspecteur d'équipe</h1>
          <p className="text-sm text-secondary mt-1">Agents du pool INTEGRIA — statuts temps réel</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm text-primary transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
          <input id="team-inspector-search" name="team-inspector-search"
            type="text"
            placeholder="Filtrer par nom ou identifiant…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="form-input w-full pl-9"
          />
        </div>
        <select id="team-inspector-pool" name="team-inspector-pool"
          value={selectedPool}
          onChange={e => setSelectedPool(e.target.value)}
          className="form-input"
        >
          <option value="__all__">Tous les pools</option>
          {poolIds.map(pid => (
            <option key={pid} value={pid}>{pid}</option>
          ))}
          {groups[''] && groups[''].length > 0 && (
            <option value="__none__">Sans pool</option>
          )}
        </select>
      </div>

      {/* Error state */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-error-light border text-error">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-medium">Impossible de charger le roster</p>
            <p className="text-sm mt-0.5">{error.message}</p>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20 text-secondary">
          <RefreshCw className="w-6 h-6 animate-spin mr-3" />
          Chargement du roster…
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <>
          <div className="text-xs text-secondary">
            {filtered.length} agent{filtered.length !== 1 ? 's' : ''} affiché{filtered.length !== 1 ? 's' : ''}
            {agents && ` sur ${agents.length}`}
          </div>
          <div className="overflow-x-auto rounded-xl border bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 text-secondary font-medium">Agent</th>
                  <th className="text-left px-4 py-3 text-secondary font-medium">Rôle</th>
                  <th className="text-left px-4 py-3 text-secondary font-medium">Type</th>
                  <th className="text-left px-4 py-3 text-secondary font-medium">Runtime</th>
                  <th className="text-left px-4 py-3 text-secondary font-medium">Clé API</th>
                  <th className="text-left px-4 py-3 text-secondary font-medium">Prompt</th>
                  <th className="text-left px-4 py-3 text-secondary font-medium">Interactions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-tertiary">
                      Aucun agent ne correspond aux filtres
                    </td>
                  </tr>
                ) : (
                  filtered.map(agent => (
                    <tr key={agent.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-medium text-primary">{agent.name}</span>
                        <span className="block text-xs text-tertiary font-mono">{agent.id}</span>
                      </td>
                      <td className="px-4 py-3 text-primary">{agent.role ?? '—'}</td>
                      <td className="px-4 py-3">
                        <TypeBadge kind={agent.kind} roster={agent.roster} poolId={agent.poolId} />
                      </td>
                      <td className="px-4 py-3">
                        <RuntimeBadge status={agent.runtime.status} />
                      </td>
                      <td className="px-4 py-3">
                        <ApiKeyBadge apiKey={agent.apiKey} />
                      </td>
                      <td className="px-4 py-3">
                        <PromptBadge prompt={agent.prompt} />
                      </td>
                      <td className="px-4 py-3 text-secondary text-xs">
                        {agent.interactions && agent.interactions.length > 0
                          ? agent.interactions.join(', ')
                          : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
